import React, { useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Radio,
    RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    usePostureInstructions,
    useCreatePostureInstruction,
    useUpdatePostureInstruction,
    useDeletePostureInstruction,
} from '../../hooks/postureInstructions';
import { useMediaFiles } from '../../hooks/mediaFiles';
import MediaFilePicker from './MediaFilePicker';
import type { PostureInstruction, PostureInstructionStatus, CreatePostureInstructionDto } from '../../types/types';
import { kPostureTypes } from '../../postureInstructionConstants';
import { t } from '../../i18n/t';

const statusLabel = (s: string) => {
    const map: Record<string, string> = {
        active: t('postureInstructions.status.active'),
        inactive: t('postureInstructions.status.inactive'),
        archived: t('postureInstructions.status.archived'),
    };
    return map[s] ?? s;
};

const statusChipColor = (s: PostureInstructionStatus): 'success' | 'default' | 'warning' => {
    if (s === 'active') return 'success';
    if (s === 'inactive') return 'warning';
    return 'default';
};

const DashboardPostureInstructions = () => {
    const { data: instructions = [], isLoading, refetch } = usePostureInstructions();
    const { data: mediaFiles = [] } = useMediaFiles();
    const deleteMutation = useDeletePostureInstruction();
    const [createOpen, setCreateOpen] = useState(false);
    const [editingInstruction, setEditingInstruction] = useState<PostureInstruction | null>(null);

    const mediaNameById = useMemo(() => {
        const m: Record<number, string> = {};
        mediaFiles.forEach((f) => { m[f.id] = f.name; });
        return m;
    }, [mediaFiles]);

    const handleDelete = async (instruction: PostureInstruction) => {
        if (!window.confirm(t('postureInstructions.deleteConfirm', { title: instruction.title }))) return;
        try {
            await deleteMutation.mutateAsync(instruction.id);
        } catch (e) {
            console.error(e);
            alert(t('postureInstructions.deleteFailed'));
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t('postureInstructions.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="contained" onClick={() => setCreateOpen(true)}>
                    {t('postureInstructions.new')}
                </Button>
                <Tooltip title={t('postureInstructions.refresh')}>
                    <IconButton onClick={() => refetch()}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>{t('postureInstructions.colType')}</TableCell>
                            <TableCell>{t('postureInstructions.colTitle')}</TableCell>
                            <TableCell>{t('postureInstructions.colOrder')}</TableCell>
                            <TableCell>{t('postureInstructions.colStatus')}</TableCell>
                            <TableCell>{t('postureInstructions.colImage')}</TableCell>
                            <TableCell align="right">{t('postureInstructions.colActions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {instructions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    {t('postureInstructions.empty')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            instructions.map((instruction) => (
                                <TableRow key={instruction.id}>
                                    <TableCell>{instruction.id}</TableCell>
                                    <TableCell>{instruction.type}</TableCell>
                                    <TableCell>{instruction.title}</TableCell>
                                    <TableCell>{instruction.order}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={statusLabel(instruction.status)}
                                            color={statusChipColor(instruction.status)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {instruction.mediaFileId != null
                                            ? (mediaNameById[instruction.mediaFileId] ?? `#${instruction.mediaFileId}`)
                                            : t('postureInstructions.imageAbsent')}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={t('common.edit')}>
                                            <IconButton
                                                size="small"
                                                onClick={() => setEditingInstruction(instruction)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('common.delete')}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(instruction)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {createOpen && (
                <PostureInstructionFormDialog
                    title={t('postureInstructions.dialogNew')}
                    onClose={() => setCreateOpen(false)}
                />
            )}
            {editingInstruction && (
                <PostureInstructionFormDialog
                    title={t('postureInstructions.dialogEdit')}
                    instruction={editingInstruction}
                    onClose={() => setEditingInstruction(null)}
                />
            )}
        </Box>
    );
};

interface PostureInstructionFormDialogProps {
    title: string;
    instruction?: PostureInstruction;
    onClose: () => void;
}

const PostureInstructionFormDialog: React.FC<PostureInstructionFormDialogProps> = ({
    title,
    instruction,
    onClose,
}) => {
    const createMutation = useCreatePostureInstruction();
    const updateMutation = useUpdatePostureInstruction();
    const isEdit = instruction != null;

    const [type, setType] = useState(instruction?.type ?? kPostureTypes[0]);
    const [titleField, setTitleField] = useState(instruction?.title ?? '');
    const [description, setDescription] = useState(instruction?.description ?? '');
    const [order, setOrder] = useState(instruction?.order ?? 1);
    const [status, setStatus] = useState<PostureInstructionStatus>(instruction?.status ?? 'active');
    const [mediaFileId, setMediaFileId] = useState<number | null>(instruction?.mediaFileId ?? null);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!type.trim()) {
            alert(t('postureInstructions.enterType'));
            return;
        }
        if (!titleField.trim()) {
            alert(t('postureInstructions.enterTitle'));
            return;
        }
        if (!description.trim()) {
            alert(t('postureInstructions.enterDescription'));
            return;
        }
        if (!Number.isInteger(order) || order < 0) {
            alert(t('postureInstructions.enterOrder'));
            return;
        }

        const dto: CreatePostureInstructionDto = {
            type: type.trim(),
            title: titleField.trim(),
            description: description.trim(),
            order,
            status,
            mediaFileId,
        };

        setSaving(true);
        try {
            if (isEdit && instruction) {
                await updateMutation.mutateAsync({ id: instruction.id, dto });
            } else {
                await createMutation.mutateAsync(dto);
            }
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('postureInstructions.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="md" onClose={onClose}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <Autocomplete
                        freeSolo
                        options={[...kPostureTypes]}
                        value={type}
                        onChange={(_, v) => setType(v ?? '')}
                        onInputChange={(_, v) => setType(v)}
                        renderInput={(params) => (
                            <TextField {...params} label={t('postureInstructions.fieldType')} size="small" />
                        )}
                    />
                    <TextField
                        label={t('postureInstructions.fieldTitle')}
                        value={titleField}
                        onChange={(e) => setTitleField(e.target.value)}
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label={t('postureInstructions.fieldDescription')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        minRows={4}
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label={t('postureInstructions.fieldOrder')}
                        type="number"
                        value={order}
                        onChange={(e) => setOrder(Number(e.target.value))}
                        size="small"
                        fullWidth
                        inputProps={{ min: 0 }}
                    />
                    <RadioGroup
                        row
                        value={status}
                        onChange={(e) => setStatus(e.target.value as PostureInstructionStatus)}
                    >
                        <FormControlLabel
                            value="active"
                            control={<Radio />}
                            label={t('postureInstructions.status.active')}
                        />
                        <FormControlLabel
                            value="inactive"
                            control={<Radio />}
                            label={t('postureInstructions.status.inactive')}
                        />
                        <FormControlLabel
                            value="archived"
                            control={<Radio />}
                            label={t('postureInstructions.status.archived')}
                        />
                    </RadioGroup>

                    <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                        <MediaFilePicker kind="image" value={mediaFileId} onChange={setMediaFileId} />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DashboardPostureInstructions;
